import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';
import Axios from '../../networking/interceptor';
import { driver_handover_requests, driver_handover_targets } from '../../networking/urlEndPoints';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { useAllSelector } from '../../services/selectors/allSelector';
import { findPharmacistById, getPharmacistDisplayName, idsMatch } from '../../utils/helper/helper';

const InstoreUnassignedHandover = ({ patient, selectedRxIds = [], isActive = true }) => {
    const { pharmacyId, selectedPharmacistID } = useAuthSelector();
    const location = useLocation();
    const { pharmacistDetail, driverDefaultPharmacistId } = useAllSelector();
    const signatureRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [targets, setTargets] = useState({ drivers: [], pharmacists: [] });
    const [target, setTarget] = useState('');
    const [recipientType, setRecipientType] = useState('pharmacist');
    const [signature, setSignature] = useState('');
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [sourcePharmacistId, setSourcePharmacistId] = useState('');

    const pharmacistOfTheDay = findPharmacistById(pharmacistDetail, driverDefaultPharmacistId);
    const patientName = [patient?.last_name, patient?.first_name].filter(Boolean).join(' ') || 'Patient';
    const isPendingMedication = String(patient?.delivery_status || '').trim().toLowerCase() === 'pending';
    const handingOverPharmacist = findPharmacistById(pharmacistDetail, sourcePharmacistId) || pharmacistOfTheDay;
    const handingOverPharmacistName = getPharmacistDisplayName(handingOverPharmacist) || 'Not selected';
    const otherPharmacists = Array.isArray(pharmacistDetail)
        ? pharmacistDetail.filter((pharmacist) => !idsMatch(pharmacist?.id, pharmacistOfTheDay?.id))
        : [];
    const selectedOtherPharmacistId = otherPharmacists.some((pharmacist) => idsMatch(pharmacist?.id, sourcePharmacistId))
        ? sourcePharmacistId
        : '';
    const isESignPage = location.pathname === '/e-sign';

    useEffect(() => {
        if (!open || !pharmacyId) return;

        Axios.get(driver_handover_targets, { params: { pharmacy_id: pharmacyId } })
            .then((response) => setTargets(response.data?.data || { drivers: [], pharmacists: [] }))
            .catch(() => setMessage('Unable to load handover recipients.'));
    }, [open, pharmacyId]);

    useEffect(() => {
        if (!open) return;
        setSourcePharmacistId(selectedPharmacistID || pharmacistOfTheDay?.id || '');
    }, [open, selectedPharmacistID, pharmacistOfTheDay?.id]);

    const patientMedicationIds = [
        ...(patient?.patients_win_rx_data || []),
        ...(patient?.rx_data || []),
        ...(patient?.patients_win_rx_data_today_missed || []),
        ...(patient?.patients_win_rx_data_final_missed || []),
    ]
        .filter((medication) => !medication?.instore_handover_accepted)
        .map((medication) => Number(medication?.id))
        .filter((id) => id > 0);
    const handoverMedicationIds = isActive && selectedRxIds.length > 0
        ? selectedRxIds.filter((id) => patientMedicationIds.includes(Number(id)))
        : patientMedicationIds;

    if (!isPendingMedication || patient?.instore_delivery_assignment?.assigned || handoverMedicationIds.length === 0) {
        return null;
    }

    const assistants = (targets.drivers || [])
        .map((item) => ({ ...item, value: `${item.type}:${item.id}` }));
    const pharmacists = (targets.pharmacists || [])
        .filter((item) => !idsMatch(item?.id, handingOverPharmacist?.id))
        .map((item) => ({ ...item, value: `${item.type}:${item.id}` }));
    const recipientOptions = recipientType === 'assistant' ? assistants : pharmacists;

    const close = () => {
        if (saving) return;
        setOpen(false);
        setTarget('');
        setRecipientType('pharmacist');
        setSignature('');
        setMessage('');
        setSourcePharmacistId('');
        signatureRef.current?.clear();
    };

    const submit = async () => {
        const [targetType, targetId] = target.split(':');
        if (!handingOverPharmacist?.id || !target || !signature) {
            setMessage('Confirm the handing-over pharmacist, recipient, and signature.');
            return;
        }

        setSaving(true);
        setMessage('');
        try {
            await Axios.post(driver_handover_requests, {
                pharmacy_id: pharmacyId,
                handover_type: 'instore_unassigned',
                patient_id: patient.id,
                winrx_data_ids: handoverMedicationIds,
                target_type: targetType,
                target_id: Number(targetId),
                source_pharmacist_id: Number(handingOverPharmacist.id),
                signature_base64: signature,
            });
            close();
        } catch (error) {
            setMessage(error?.response?.data?.data?.error?.handover?.[0] || error?.response?.data?.message || 'Unable to send handover request.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Hand over medication"
                className={`inline-flex min-h-7 items-center justify-center gap-0.5 rounded-md border border-indigo-200 bg-indigo-50 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 active:bg-indigo-200 ${isESignPage ? 'h-7 w-7 px-0' : 'px-2'}`}
                style={{ minHeight: '28px', fontSize: '11px', lineHeight: 1 }}
                aria-label="Hand over medication"
            >
                <svg aria-hidden="true" className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                {!isESignPage ? <span>Hand over</span> : null}
            </button>

            {createPortal(
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Medication handover"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                    <motion.button
                        type="button"
                        className="absolute inset-0 cursor-default"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }}
                        aria-label="Close handover"
                        onClick={close}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />
                    <motion.div
                        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-y-auto rounded-lg bg-white shadow-xl"
                        initial={{ opacity: 0, scale: 0.96, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 12 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Handover medication for {patientName}</h3>
                                <p className="mt-1 text-sm text-gray-600">Choose who will receive the selected medication.</p>
                            </div>
                            <button
                                type="button"
                                onClick={close}
                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                aria-label="Close handover dialog"
                            >
                                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4 p-5">
                            <div className="rounded-md border border-gray-200 bg-white p-3">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm font-semibold text-gray-900">Handing Over As</span>
                                        <span className="inline-flex items-center rounded border border-green-100 bg-green-50 px-3 py-1 text-lg font-semibold text-green-700">
                                            {handingOverPharmacistName}
                                        </span>
                                    </div>
                                    <label className="flex flex-col gap-1 text-sm font-semibold text-gray-900">
                                        <span>{pharmacistOfTheDay?.id ? `Not ${getPharmacistDisplayName(pharmacistOfTheDay)} ?` : 'Choose pharmacist'}</span>
                                        <select
                                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                            value={selectedOtherPharmacistId}
                                            onChange={(event) => setSourcePharmacistId(event.target.value || pharmacistOfTheDay?.id || '')}
                                        >
                                            <option value="">{pharmacistOfTheDay?.id ? 'Choose another pharmacist' : 'Select pharmacist'}</option>
                                            {otherPharmacists.map((pharmacist) => (
                                                <option key={pharmacist.id} value={pharmacist.id}>
                                                    {getPharmacistDisplayName(pharmacist) || pharmacist?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <p className="mb-2 text-sm font-semibold text-gray-800">Send to</p>
                                <div className="mb-3 grid grid-cols-2 gap-2">
                                    {[{ key: 'pharmacist', label: 'Pharmacist' }, { key: 'assistant', label: 'Assistant' }].map((option) => (
                                        <button
                                            key={option.key}
                                            type="button"
                                            onClick={() => {
                                                setRecipientType(option.key);
                                                setTarget('');
                                                setMessage('');
                                            }}
                                            className={`rounded-md border px-3 py-2 text-sm font-medium transition ${recipientType === option.key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                                <select
                                    value={recipientOptions.some((item) => item.value === target) ? target : ''}
                                    onChange={(event) => setTarget(event.target.value)}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                                >
                                    <option value="">Select {recipientType}</option>
                                    {recipientOptions.map((item) => <option key={item.value} value={item.value}>{item.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <p className="mb-1 text-sm font-semibold text-gray-800">Pharmacist signature</p>
                                <div className="overflow-hidden rounded-md border border-gray-300 bg-white">
                                    <SignatureCanvas
                                        ref={signatureRef}
                                        penColor="black"
                                        clearOnResize={false}
                                        canvasProps={{ className: 'h-36 w-full' }}
                                        onEnd={() => setSignature(signatureRef.current?.toDataURL() || '')}
                                    />
                                </div>
                                <button type="button" onClick={() => { signatureRef.current?.clear(); setSignature(''); }} className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900">Clear signature</button>
                            </div>

                            {message ? <p className="text-sm text-red-600">{message}</p> : null}
                        </div>

                        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-5 py-4">
                            <button type="button" onClick={close} disabled={saving} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
                            <button type="button" onClick={submit} disabled={saving} className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">{saving ? 'Sending...' : 'Send handover'}</button>
                        </div>
                    </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>,
            document.body
            )}
        </>
    );
};

export default InstoreUnassignedHandover;
